<template>
  <div class="voice-record-page">
    <div class="settings-card">
      <h2 class="page-title">
        <Icon icon="heroicons:speaker-wave" width="24" height="24" />
        声音合成记录
      </h2>

      <!-- 搜索区域 -->
      <div class="search-bar">
        <div class="search-left">
          <el-input v-model="search.keyword" placeholder="按任务名/音色ID搜索" clearable size="large" class="search-input"
            @keyup.enter="doSearch">
            <template #prefix>
              <Icon icon="heroicons:magnifying-glass" width="18" height="18" />
            </template>
          </el-input>

          <el-button type="primary" size="large" @click="doSearch" class="search-btn">
            <Icon icon="heroicons:magnifying-glass" width="18" height="18" />
            <span class="btn-text">搜索</span>
          </el-button>

          <el-button size="large" @click="fetchList" :loading="loading" plain>
            <Icon icon="heroicons:arrow-path" width="18" height="18" />
            <span class="btn-text">刷新</span>
          </el-button>

          <el-popconfirm v-if="selectedIds.length" :title="`确认删除选中的 ${selectedIds.length} 条记录吗？`"
            @confirm="doBatchDelete">
            <template #reference>
              <el-button type="danger" :disabled="!selectedIds.length" size="large">
                <Icon icon="heroicons:trash" width="18" height="18" />
                <span class="btn-text">批量删除（{{ selectedIds.length }}）</span>
              </el-button>
            </template>
          </el-popconfirm>
        </div>

        <div class="search-right">
          <el-button size="large"
            :disabled="!selectedIds.length || !hasSelectedAudios"
            :loading="zipMaking" @click="downloadBatchZip" plain>
            <Icon icon="heroicons:arrow-down-tray" width="18" height="18" />
            <span class="btn-text">批量打包下载</span>
          </el-button>

          <el-button type="success" size="large" @click="openAdd" class="add-btn">
            <Icon icon="heroicons:plus" width="18" height="18" />
            <span class="btn-text">添加合成</span>
          </el-button>
        </div>
      </div>

      <!-- 表格包装器 -->
      <div class="table-wrapper">
        <el-table :data="list" stripe v-loading="loading" class="data-table" @selection-change="handleSelectionChange"
          :row-style="{ background: 'transparent' }" :cell-style="{ background: 'transparent' }"
          :header-cell-style="{ background: 'rgba(157, 78, 221, 0.15)', color: '#fff' }">

          <el-table-column type="selection" width="48" />
          <el-table-column prop="id" label="ID" width="70" />
          <el-table-column prop="voice_task_name" label="任务名" min-width="200" show-overflow-tooltip />
          <el-table-column prop="voice_clone_id" label="合成语音（音色ID）" min-width="220" show-overflow-tooltip />

          <el-table-column label="音频" min-width="320">
            <template #default="{ row }">
              <div v-if="row.human_voice_synthetic_url" class="audio-cell">
                <audio :src="row.human_voice_synthetic_url" controls preload="metadata" class="audio-player"></audio>
                <button class="icon-btn" @click="copyText(row.human_voice_synthetic_url)">
                  <Icon icon="heroicons:document-duplicate" width="14" height="14" />
                </button>
              </div>
              <span v-else class="empty-text">—</span>
            </template>
          </el-table-column>

          <el-table-column label="状态" width="100">
            <template #default="{ row }">
              <span class="type-tag" :class="getStatusClass(row.status)">
                {{ getStatusText(row.status) }}
              </span>
            </template>
          </el-table-column>

          <el-table-column prop="create_time" label="创建时间" width="170" />

          <el-table-column label="操作" width="220" fixed="right">
            <template #default="{ row }">
              <button v-if="row.human_voice_synthetic_url" class="action-btn primary-btn" @click="downloadOne(row)">
                <Icon icon="heroicons:arrow-down-tray" width="14" height="14" />
                <span class="btn-text">下载</span>
              </button>
              <button class="action-btn danger-btn" @click="delOne(row.id)">
                <Icon icon="heroicons:trash" width="14" height="14" />
                <span class="btn-text">删除</span>
              </button>
            </template>
          </el-table-column>
        </el-table>
      </div>

      <!-- 分页组件 -->
      <div class="pagination-container">
        <el-pagination v-model:current-page="pager.page" v-model:page-size="pager.pageSize" :total="pager.total"
          :page-sizes="[10, 20, 30, 50]" :layout="paginationLayout" @size-change="fetchList"
          @current-change="fetchList" background />
      </div>
    </div>

    <!-- 新增合成弹窗 -->
    <el-dialog v-model="dialogVisible" title="声音合成" :width="dialogWidth" :close-on-click-modal="false"
      destroy-on-close class="custom-dialog">
      <el-form ref="formRef" :model="form" :rules="rules" :label-width="formLabelWidth" class="clone-form">
        <el-form-item label="任务名" prop="voice_task_name">
          <el-input v-model="form.voice_task_name" placeholder="任务名，随便写一个吧" size="large">
            <template #append>
              <button class="input-append-btn" @click="insertComma('voice_task_name')" type="button">
                <Icon icon="heroicons:plus" width="14" height="14" />
                插入分隔符
              </button>
            </template>
          </el-input>
        </el-form-item>

        <el-form-item label="发音人" prop="voice_clone_id">
          <el-select v-model="form.voice_clone_id" multiple filterable collapse-tags collapse-tags-tooltip
            placeholder="可多选；默认轮询分配到每行文字" style="width: 100%" size="large">
            <el-option v-for="sp in speakers" :key="sp.voice_id"
              :label="`${sp.voice_name}（${sp.voice_id}）`" :value="sp.voice_id" />
          </el-select>
          <div class="form-hint">
            说明：默认<b>轮询分配</b>；若勾选"全员合成"，则每一行会对所有发音人各合成一次。
          </div>
        </el-form-item>

        <el-form-item label="策略">
          <label class="checkbox-label">
            <input type="checkbox" v-model="form.apply_to_all" />
            <span>全员合成：每一行文本对所有选中的发音人都合成</span>
          </label>
        </el-form-item>

        <el-form-item label="文字" prop="voice_text">
          <el-input v-model="form.voice_text" type="textarea" :rows="6"
            placeholder="文字转语音：每一行将单独合成一条" class="textarea-large" />
          <div class="inline-actions">
            <div class="form-hint">已输入字数：{{ baseChars }}</div>
            <div class="form-hint">
              块数（20字/块，向上取整）：
              {{ form.apply_to_all ? `${baseChunks} × ${Math.max(1, form.voice_clone_id.length)} = ${totalChunks}` : totalChunks }}
            </div>
            <button class="link-btn" type="button" @click="insertNewline('voice_text')">
              <Icon icon="heroicons:plus" width="14" height="14" />
              插入换行
            </button>
          </div>
          <div class="price-tip warn-tip">
            <Icon icon="heroicons:exclamation-triangle" width="14" height="14" />
            如需音色用于数字人合成视频上，建议输入不超过500字
          </div>

          <!-- 预览 -->
          <div v-if="textList.length && form.voice_clone_id.length" class="preview-box">
            <div class="preview-title">
              <Icon icon="heroicons:eye" width="14" height="14" />
              分配预览
            </div>
            <template v-if="!form.apply_to_all">
              <div class="preview-list">轮询预览（前 5 行）：</div>
              <ul class="preview-ul">
                <li v-for="(t, idx) in textList.slice(0, 5)" :key="idx">
                  第 {{ idx + 1 }} 行 → {{ speakerLabelByIndex(idx) }}
                </li>
              </ul>
            </template>
            <template v-else>
              <div class="preview-list">全员合成预览（前 3 行 × 所有人）：</div>
              <ul class="preview-ul">
                <li v-for="(t, idx) in textList.slice(0, 3)" :key="idx">
                  第 {{ idx + 1 }} 行 → {{ speakers.map(s => s.voice_name).join('、') }}
                </li>
              </ul>
            </template>
          </div>
        </el-form-item>

        <el-form-item>
          <label class="checkbox-label">
            <input type="checkbox" v-model="agree" :disabled="!agreeEnabled" />
            <span>我已阅读并同意</span>
            <button class="link-btn" type="button" @click.prevent="openAgreement">
              《克隆使用协议》
            </button>
          </label>
          <span v-if="agreeCountdown > 0" class="countdown-text">（请至少阅读 {{ agreeCountdown }} 秒）</span>
        </el-form-item>

        <div class="price-tip">
          <Icon icon="heroicons:information-circle" width="16" height="16" />
          {{ priceTip }}
        </div>
      </el-form>

      <template #footer>
        <button class="dialog-btn cancel-btn" @click="dialogVisible = false">取消</button>
        <button class="dialog-btn confirm-btn"
          :disabled="!agreeReady || textList.length === 0 || form.voice_clone_id.length === 0 || submitting"
          @click="submit">
          <Icon icon="heroicons:sparkles" width="16" height="16" />
          合成语音（预计 {{ totalCost }} 积分 / {{ baseChars }}字）
        </button>
      </template>
    </el-dialog>

    <!-- 协议弹窗 -->
    <el-dialog v-model="agreementDialogVisible" :title="agreementTitle || '克隆使用协议'" :width="dialogWidth"
      :close-on-click-modal="false" @closed="stopAgreeCountdown" class="custom-dialog">
      <div v-loading="agreementLoading" class="agreement-content">
        <div class="agreement-meta" v-if="agreementUpdatedAt">
          最近更新：{{ agreementUpdatedAt }}
        </div>
        <div class="agreement-text" v-if="agreementContent" v-html="agreementContent"></div>
        <el-empty v-else description="未读取到协议内容" />
      </div>
      <template #footer>
        <button class="dialog-btn cancel-btn" @click="agreementDialogVisible = false">关闭</button>
        <button class="dialog-btn confirm-btn" :disabled="agreeCountdown > 0" @click="acceptAgreement">
          {{ agreeCountdown > 0 ? `请阅读（${agreeCountdown}s）` : '我已阅读并同意' }}
        </button>
      </template>
    </el-dialog>
  </div>
</template>

<script>
import axios from 'axios'
import Cookies from 'js-cookie'
import config from '@/config.js'
import { Icon } from '@iconify/vue'
import { ElMessage, ElMessageBox } from 'element-plus'
import { saveAs } from 'file-saver'

export default {
  name: 'VoiceRecord',
  components: { Icon },
  data() {
    return {
      loading: false,
      list: [],
      pager: { page: 1, pageSize: 10, total: 0 },
      search: { keyword: '' },
      selectedIds: [],
      speakers: [],
      dialogVisible: false,
      submitting: false,
      zipMaking: false,
      agree: false,
      agreeEnabled: false,
      agreeCountdown: 0,
      agreeTimer: null,
      agreementDialogVisible: false,
      agreementLoading: false,
      agreementTitle: '',
      agreementUpdatedAt: '',
      agreementContent: '',
      form: {
        voice_task_name: '',
        voice_clone_id: [],
        voice_text: '',
        apply_to_all: false
      },
      rules: {
        voice_task_name: [{ required: true, message: '请输入任务名', trigger: 'blur' }],
        voice_clone_id: [{ type: 'array', required: true, message: '请至少选择 1 位发音人', trigger: 'change' }],
        voice_text: [{ required: true, message: '请输入要合成的文字', trigger: 'blur' }]
      },
      unitPer20: 0,
      screenWidth: window.innerWidth
    }
  },
  computed: {
    userInfo() {
      return JSON.parse(Cookies.get('user_info') || '{}')
    },
    nameList() {
      return this.splitByComma(this.form.voice_task_name)
    },
    textList() {
      return this.splitByNewline(this.form.voice_text)
    },
    hasSelectedAudios() {
      return this.list.some(r => this.selectedIds.includes(r.id) && r.human_voice_synthetic_url)
    },
    baseChars() {
      return this.textList.reduce((n, t) => n + this.charCount(t), 0)
    },
    baseChunks() {
      return this.textList.reduce((n, t) => n + this.chunksOfText(t), 0)
    },
    totalChunks() {
      const base = this.baseChunks
      return this.form.apply_to_all ? base * Math.max(1, this.form.voice_clone_id.length) : base
    },
    totalCost() {
      return (this.totalChunks * this.unitPer20).toFixed(2)
    },
    priceTip() {
      const p1 = `单价：${this.unitPer20} 积分 / 20字`
      const p2 = this.form.apply_to_all
        ? `；预计：(${this.baseChunks} 块 × 发音人数 ${Math.max(1, this.form.voice_clone_id.length)}) × ${this.unitPer20} = ${this.totalCost} 积分`
        : `；预计：${this.totalChunks} 块 × ${this.unitPer20} = ${this.totalCost} 积分`
      return p1 + p2
    },
    agreeReady() {
      return this.agree && this.agreeCountdown === 0
    },
    paginationLayout() {
      if (this.screenWidth < 576) return 'prev, pager, next'
      if (this.screenWidth < 768) return 'total, prev, pager, next'
      return 'total, sizes, prev, pager, next, jumper'
    },
    dialogWidth() {
      if (this.screenWidth < 576) return '95%'
      if (this.screenWidth < 768) return '90%'
      return '800px'
    },
    formLabelWidth() {
      if (this.screenWidth < 576) return '80px'
      return '100px'
    }
  },
  mounted() {
    this.fetchList()
    this.fetchSpeakers()
    window.addEventListener('resize', this.handleResize)
  },
  beforeUnmount() {
    window.removeEventListener('resize', this.handleResize)
    this.stopAgreeCountdown()
  },
  methods: {
    handleResize() {
      this.screenWidth = window.innerWidth
    },
    truncateUrl(url) {
      if (!url) return ''
      if (url.length > 40) {
        return url.substring(0, 25) + '...' + url.substring(url.length - 12)
      }
      return url
    },
    splitByComma(s) {
      return (s || '').split(/[，,]/g).map(x => x.trim()).filter(Boolean)
    },
    splitByNewline(s) {
      return (s || '').split(/\r?\n/).map(x => x.trim()).filter(Boolean)
    },
    insertComma(field) {
      const v = (this.form[field] || '')
      const ends = /[，,]\s*$/.test(v)
      this.form[field] = ends ? v + ' ' : (v ? v + ', ' : ', ')
    },
    insertNewline(field) {
      const v = (this.form[field] || '')
      this.form[field] = v.endsWith('\n') || v === '' ? v : v + '\n'
    },
    charCount(s) {
      return (s || '').replace(/\s+/g, '').length
    },
    chunksOfText(s) {
      const c = this.charCount(s)
      return c === 0 ? 0 : Math.ceil(c / 20)
    },
    getStatusClass(status) {
      if (status === 1) return 'status-success'
      if (status === 3) return 'status-processing'
      return 'status-failed'
    },
    getStatusText(status) {
      if (status === 1) return '成功'
      if (status === 3) return '处理中'
      return '失败'
    },
    copyText(text) {
      if (!text) return
      if (navigator.clipboard && window.isSecureContext) {
        navigator.clipboard.writeText(text).then(() => ElMessage.success('已复制'))
      } else {
        const ta = document.createElement('textarea')
        ta.value = text
        ta.style.position = 'fixed'
        ta.style.left = '-9999px'
        document.body.appendChild(ta)
        ta.select()
        try {
          document.execCommand('copy')
          ElMessage.success('已复制')
        } catch (e) {
          ElMessage.error('复制失败')
        } finally {
          document.body.removeChild(ta)
        }
      }
    },
    speakerLabelByIndex(i) {
      if (!this.speakers?.length || !this.form.voice_clone_id?.length) return '未选择'
      const vid = this.form.voice_clone_id[i % this.form.voice_clone_id.length]
      const found = this.speakers.find(s => s.voice_id === vid)
      return found ? `${found.voice_name}（${found.voice_id}）` : vid
    },
    buildSafeName(name) {
      return String(name)
        .replace(/[\\/:*?"<>|]/g, '_')
        .replace(/\s+/g, '_')
        .slice(0, 120)
    },
    forceDownload(url, filename) {
      const a = document.createElement('a')
      a.href = url
      a.download = filename || 'audio.mp3'
      document.body.appendChild(a)
      a.click()
      document.body.removeChild(a)
    },
    downloadOne(row) {
      if (!row?.human_voice_synthetic_url) {
        ElMessage.warning('没有可下载的音频')
        return
      }
      this.forceDownload(row.human_voice_synthetic_url, `${row.voice_task_name || 'audio'}_${row.id}.mp3`)
    },
    async downloadBatchZip() {
      const rows = this.list
        .filter(r => this.selectedIds.includes(r.id))
        .filter(r => r.human_voice_synthetic_url)
      if (!rows.length) {
        ElMessage.warning('请选择至少一个已合成的音频')
        return
      }
      this.zipMaking = true
      try {
        const items = rows.map(r => ({
          url: r.human_voice_synthetic_url,
          filename: this.buildSafeName(`${r.voice_task_name || 'audio'}_${r.id}.mp3`)
        }))
        const res = await fetch(`${config.BASE_URL}/shop/api/zip_videos.php`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ items })
        })
        if (!res.ok) throw new Error(`HTTP ${res.status}`)
        const blob = await res.blob()
        saveAs(blob, `audios_${new Date().toISOString().slice(0, 19).replace(/[:T]/g, '')}.zip`)
        ElMessage.success(`打包完成，共 ${rows.length} 个音频`)
      } catch (e) {
        console.error(e)
        ElMessage.error('打包失败，请重试')
      } finally {
        this.zipMaking = false
      }
    },
    async fetchList() {
      try {
        this.loading = true
        const { data } = await axios.post(`${config.BASE_URL}/shop/api/voice_clone_synthetic_record.php`, {
          action: this.search.keyword ? 'search' : 'list',
          store_id: this.userInfo.id,
          keyword: this.search.keyword,
          page: this.pager.page,
          page_size: this.pager.pageSize
        })
        this.list = data.data || []
        this.pager.total = data.pagination ? data.pagination.total : (this.list?.length || 0)
        if (!this.list.length && this.pager.page > 1) {
          this.pager.page = this.pager.page - 1
          await this.fetchList()
        }
        this.selectedIds = []
      } catch (e) {
        ElMessage.error('获取列表失败')
      } finally {
        this.loading = false
      }
    },
    doSearch() {
      this.pager.page = 1
      this.fetchList()
    },
    handleSelectionChange(rows) {
      this.selectedIds = rows.map(r => r.id)
    },
    async doBatchDelete() {
      if (!this.selectedIds.length) return
      try {
        this.loading = true
        const { data } = await axios.post(`${config.BASE_URL}/shop/api/voice_clone_synthetic_record.php`, {
          action: 'batch_delete',
          store_id: this.userInfo.id,
          ids: this.selectedIds
        })
        if (data.success) {
          ElMessage.success('批量删除成功')
          await this.fetchList()
        } else {
          ElMessage.error(data.error || '批量删除失败')
        }
      } catch (e) {
        ElMessage.error('批量删除失败')
      } finally {
        this.loading = false
      }
    },
    async delOne(id) {
      try {
        await ElMessageBox.confirm('确定删除该记录吗？', '提示', { type: 'warning', confirmButtonText: '确认删除', cancelButtonText: '取消' })
        const { data } = await axios.post(`${config.BASE_URL}/shop/api/voice_clone_synthetic_record.php`, {
          action: 'delete',
          id,
          store_id: this.userInfo.id
        })
        if (data.success) {
          ElMessage.success('已删除')
          this.fetchList()
        } else {
          ElMessage.error(data.error || '删除失败')
        }
      } catch (e) {
        if (e !== 'cancel') ElMessage.error('删除失败')
      }
    },
    async fetchSpeakers() {
      try {
        const { data } = await axios.post(`${config.BASE_URL}/shop/api/voice_clone.php`, {
          action: 'list_all',
          store_id: this.userInfo.id
        })
        const rows = data?.data || []
        this.speakers = rows
          .filter(r => r.voice_id)
          .map(r => ({ voice_id: r.voice_id, voice_name: r.voice_name || r.voice_id }))
      } catch (e) {
        this.speakers = []
      }
    },
    async openAdd() {
      this.dialogVisible = true
      this.agree = false
      this.agreeEnabled = false
      this.stopAgreeCountdown()
      this.form = { voice_task_name: '', voice_clone_id: [], voice_text: '', apply_to_all: false }
      this.$nextTick(() => this.$refs.formRef?.clearValidate?.())
      try {
        const { data } = await axios.get(`${config.BASE_URL}/shop/api/get_digital_human_config.php`)
        const v = Number(parseFloat(data?.data?.digital_human_voice_synth_num || 0).toFixed(2))
        this.unitPer20 = isNaN(v) ? 0 : v
      } catch {
        this.unitPer20 = 0
      }
    },
    async openAgreement() {
      try {
        this.agreementDialogVisible = true
        this.agreementLoading = true
        const { data } = await axios.get(`${config.BASE_URL}/shop/api/get_clone_agreement.php`)
        if (data?.code === 200 && data?.data) {
          this.agreementTitle = data.data.title || '克隆使用协议'
          this.agreementUpdatedAt = data.data.updated_at || ''
          this.agreementContent = data.data.content || ''
        } else {
          this.agreementTitle = '克隆使用协议'
          this.agreementUpdatedAt = ''
          this.agreementContent = ''
        }
      } catch (e) {
        ElMessage.error('读取使用协议失败')
        this.agreementContent = ''
      } finally {
        this.agreementLoading = false
        this.startAgreeCountdown(3)
      }
    },
    startAgreeCountdown(sec) {
      this.stopAgreeCountdown()
      this.agreeCountdown = sec
      this.agreeTimer = setInterval(() => {
        if (this.agreeCountdown > 0) this.agreeCountdown--
        if (this.agreeCountdown <= 0) this.stopAgreeCountdown()
      }, 1000)
    },
    stopAgreeCountdown() {
      if (this.agreeTimer) clearInterval(this.agreeTimer)
      this.agreeTimer = null
    },
    acceptAgreement() {
      if (this.agreeCountdown > 0) return
      this.agreementDialogVisible = false
      this.agreeEnabled = true
      this.agree = true
    },
    async submit() {
      this.$refs.formRef.validate(async (ok) => {
        if (!ok) return
        if (!this.agreeReady) {
          ElMessage.warning('请先阅读并同意克隆使用协议')
          return
        }
        if (this.form.voice_clone_id.length === 0) {
          ElMessage.warning('请选择发音人')
          return
        }
        if (this.textList.length === 0) {
          ElMessage.warning('请至少输入一行文字')
          return
        }
        this.submitting = true
        try {
          const payload = {
            action: 'add',
            store_id: this.userInfo.id,
            voice_task_name: this.nameList.length > 1 ? this.nameList : (this.nameList[0] || ''),
            voice_clone_id: this.form.voice_clone_id.length > 1 ? this.form.voice_clone_id : (this.form.voice_clone_id[0] || ''),
            voice_text: this.textList.length > 1 ? this.textList : (this.textList[0] || ''),
            apply_to_all: this.form.apply_to_all
          }
          const { data } = await axios.post(`${config.BASE_URL}/shop/api/voice_clone_synthetic_record.php`, payload)
          if (data.success) {
            ElMessage.success('已提交合成任务')
            this.dialogVisible = false
            this.fetchList()
          } else {
            ElMessage.error(data.error || '提交失败')
          }
        } catch (e) {
          ElMessage.error('提交失败')
        } finally {
          this.submitting = false
        }
      })
    }
  }
}
</script>

<style scoped lang="scss">
.voice-record-page {
  width: 100%;
  max-width: 100%;
  box-sizing: border-box;
  overflow-x: hidden;
}

.settings-card {
  background: rgba(18, 14, 33, 0.6);
  backdrop-filter: blur(10px);
  border-radius: 20px;
  border: 1px solid rgba(157, 78, 221, 0.2);
  padding: 24px;
  width: 100%;
  box-sizing: border-box;

  @media (max-width: 768px) {
    padding: 16px;
  }

  @media (max-width: 576px) {
    padding: 12px;
  }
}

.page-title {
  font-size: 20px;
  font-weight: 600;
  color: #fff;
  margin-bottom: 24px;
  display: flex;
  align-items: center;
  gap: 10px;

  svg {
    color: #c084fc;
  }

  @media (max-width: 768px) {
    font-size: 18px;
    margin-bottom: 16px;
  }
}

.search-bar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 16px;
  margin-bottom: 20px;

  .search-left {
    display: flex;
    align-items: center;
    gap: 12px;
    flex-wrap: wrap;
    flex: 1;
  }

  .search-right {
    display: flex;
    align-items: center;
    gap: 12px;
    flex-wrap: wrap;
  }

  .search-input {
    flex: 1;
    min-width: 200px;
  }

  @media (max-width: 768px) {
    flex-direction: column;

    .search-left,
    .search-right {
      width: 100%;
    }

    .search-left .el-input {
      width: 100% !important;
    }

    .add-btn {
      width: 100%;
    }
  }
}

.table-wrapper {
  position: relative;
  width: 100%;
  overflow-x: auto;
  margin-top: 20px;
  -webkit-overflow-scrolling: touch;

  &::-webkit-scrollbar {
    height: 8px;
  }

  &::-webkit-scrollbar-track {
    background: rgba(157, 78, 221, 0.1);
    border-radius: 4px;
  }

  &::-webkit-scrollbar-thumb {
    background: rgba(157, 78, 221, 0.4);
    border-radius: 4px;

    &:hover {
      background: rgba(157, 78, 221, 0.6);
    }
  }
}

:deep(.data-table) {
  --el-table-bg-color: transparent !important;
  --el-table-tr-bg-color: transparent !important;
  --el-table-header-bg-color: transparent !important;
  --el-table-row-hover-bg-color: rgba(157, 78, 221, 0.1) !important;
  --el-table-current-row-bg-color: rgba(157, 78, 221, 0.15) !important;
  --el-table-stripe-row-bg-color: rgba(157, 78, 221, 0.05) !important;
  --el-table-border-color: rgba(157, 78, 221, 0.2) !important;

  background-color: transparent !important;
  color: #e0e0e0;

  &::before {
    display: none;
  }

  .el-table__header-wrapper {
    background-color: transparent !important;

    tr {
      background-color: transparent !important;

      th:not(.el-table-fixed-column--right) {
        background: rgba(157, 78, 221, 0.2) !important;
        color: #fff !important;
        font-weight: 600;
        border-bottom: 1px solid rgba(157, 78, 221, 0.3) !important;

        .cell {
          color: #fff !important;
        }
      }
    }
  }

  .el-table__body-wrapper {
    background-color: transparent !important;

    tr {
      background-color: transparent !important;

      td:not(.el-table-fixed-column--right) {
        background-color: transparent !important;
        border-bottom: 1px solid rgba(157, 78, 221, 0.1);
        color: #e0e0e0;
      }

      &.el-table__row--striped td:not(.el-table-fixed-column--right) {
        background-color: rgba(157, 78, 221, 0.05) !important;
      }

      &:hover td:not(.el-table-fixed-column--right) {
        background-color: rgba(157, 78, 221, 0.1) !important;
      }
    }
  }

  .el-table-fixed-column--right {
    background-color: #120e21 !important;
    backdrop-filter: none !important;
  }

  th.el-table-fixed-column--right {
    background-color: #2a1f45 !important;
  }

  .el-table__row--striped .el-table-fixed-column--right {
    background-color: #1a1430 !important;
  }

  .el-table__empty-block {
    background-color: transparent !important;

    .el-table__empty-text {
      color: #a0a0c0 !important;
    }
  }
}

.type-tag {
  display: inline-block;
  padding: 4px 10px;
  border-radius: 6px;
  font-size: 12px;
  font-weight: 500;

  &.status-success {
    background: rgba(74, 222, 128, 0.15);
    color: #4ade80;
  }

  &.status-processing {
    background: rgba(157, 78, 221, 0.15);
    color: #c084fc;
  }

  &.status-failed {
    background: rgba(239, 68, 68, 0.15);
    color: #f87171;
  }
}

.empty-text {
  color: #7a7a9a;
}

.audio-cell {
  display: flex;
  align-items: center;
  gap: 8px;

  .audio-player {
    height: 36px;
    width: 180px;
    border-radius: 20px;
  }
}

.icon-btn {
  background: rgba(157, 78, 221, 0.15);
  border: none;
  border-radius: 6px;
  padding: 6px;
  cursor: pointer;
  color: #c084fc;
  display: inline-flex;
  align-items: center;
  justify-content: center;

  &:hover {
    background: rgba(157, 78, 221, 0.25);
  }
}

.action-btn {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 6px 12px;
  font-size: 12px;
  font-weight: 500;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.2s ease;
  border: none;
  margin: 0 2px;

  &.primary-btn {
    background: linear-gradient(135deg, #6546b4, #9d4edd);
    color: #fff;

    &:hover {
      transform: translateY(-1px);
    }
  }

  &.danger-btn {
    background: rgba(239, 68, 68, 0.15);
    border: 1px solid rgba(239, 68, 68, 0.4);
    color: #f87171;

    &:hover {
      background: rgba(239, 68, 68, 0.25);
    }
  }

  @media (max-width: 576px) {
    .btn-text {
      display: none;
    }
  }
}

.pagination-container {
  margin-top: 24px;
  display: flex;
  justify-content: flex-end;

  @media (max-width: 768px) {
    justify-content: center;
  }
}

:deep(.el-pagination) {
  --el-pagination-bg-color: transparent;
  --el-pagination-button-bg-color: rgba(157, 78, 221, 0.1);
  --el-pagination-hover-color: #c084fc;

  .btn-prev,
  .btn-next,
  .el-pager li {
    background: rgba(157, 78, 221, 0.1);
    border: 1px solid rgba(157, 78, 221, 0.3);
    color: #a0a0c0;
    border-radius: 8px;
    min-width: 32px;
    height: 32px;

    &:hover {
      background: rgba(157, 78, 221, 0.2);
      color: #c084fc;
    }

    &.active {
      background: linear-gradient(135deg, #6546b4, #9d4edd);
      color: #fff;
      border-color: transparent;
    }
  }

  .el-pagination__total,
  .el-pagination__jump {
    color: #a0a0c0;
  }

  @media (max-width: 576px) {
    .btn-prev,
    .btn-next,
    .el-pager li {
      min-width: 28px;
      height: 28px;
      margin: 0 2px;
    }
  }
}

:deep(.custom-dialog) {
  --el-dialog-bg-color: rgba(18, 14, 33, 0.98);
  --el-dialog-border-radius: 20px;

  .el-dialog__header {
    border-bottom: 1px solid rgba(157, 78, 221, 0.2);
    padding: 20px 24px;

    .el-dialog__title {
      color: #fff;
      font-weight: 600;
    }
  }

  .el-dialog__body {
    padding: 24px;
  }

  .el-dialog__footer {
    border-top: 1px solid rgba(157, 78, 221, 0.2);
    padding: 16px 24px;
  }

  @media (max-width: 576px) {
    .el-dialog__header {
      padding: 16px;
    }
    .el-dialog__body {
      padding: 16px;
    }
    .el-dialog__footer {
      padding: 12px 16px;
    }
  }
}

.clone-form {
  :deep(.el-form-item__label) {
    color: #a0a0c0;
    font-weight: 500;
  }

  :deep(.el-input__wrapper) {
    background: rgba(26, 21, 53, 0.8);
    border: 1px solid rgba(157, 78, 221, 0.3);
    border-radius: 10px;
    box-shadow: none;

    &:hover {
      border-color: rgba(157, 78, 221, 0.6);
    }

    &.is-focus {
      border-color: #9d4edd;
      box-shadow: 0 0 0 2px rgba(157, 78, 221, 0.2);
    }

    .el-input__inner {
      color: #fff;
      background: transparent;
    }
  }

  :deep(.el-textarea__inner) {
    background: rgba(26, 21, 53, 0.8);
    border: 1px solid rgba(157, 78, 221, 0.3);
    border-radius: 10px;
    color: #fff;

    &:hover {
      border-color: rgba(157, 78, 221, 0.6);
    }

    &:focus {
      border-color: #9d4edd;
      box-shadow: 0 0 0 2px rgba(157, 78, 221, 0.2);
    }
  }

  :deep(.el-select .el-input__wrapper) {
    background: rgba(26, 21, 53, 0.8);
  }
}

.input-append-btn {
  background: rgba(157, 78, 221, 0.2);
  border: none;
  color: #c084fc;
  padding: 8px 12px;
  border-radius: 8px;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 4px;
  font-size: 13px;

  &:hover {
    background: rgba(157, 78, 221, 0.3);
  }
}

.checkbox-label {
  display: flex;
  align-items: center;
  gap: 6px;
  cursor: pointer;
  color: #a0a0c0;
  font-size: 13px;

  input {
    cursor: pointer;
    accent-color: #9d4edd;
  }
}

.link-btn {
  background: none;
  border: none;
  color: #c084fc;
  cursor: pointer;
  padding: 0;
  font-size: 13px;
  display: inline-flex;
  align-items: center;
  gap: 4px;

  &:hover {
    text-decoration: underline;
  }
}

.form-hint {
  font-size: 12px;
  color: #7a7a9a;
  margin-top: 4px;
}

.countdown-text {
  margin-left: 8px;
  font-size: 12px;
  color: #fbbf24;
}

.price-tip {
  margin-top: 16px;
  margin-bottom: 8px;
  padding: 10px 14px;
  background: rgba(157, 78, 221, 0.1);
  border-radius: 10px;
  font-size: 13px;
  color: #a0a0c0;
  display: flex;
  align-items: center;
  gap: 8px;

  &.warn-tip {
    background: rgba(245, 158, 11, 0.1);
    color: #fbbf24;
  }
}

.inline-actions {
  display: flex;
  align-items: center;
  gap: 16px;
  margin-top: 8px;
  flex-wrap: wrap;
}

.textarea-large {
  :deep(textarea) {
    min-height: 140px;
  }
}

.preview-box {
  margin-top: 12px;
  padding: 12px;
  background: rgba(157, 78, 221, 0.08);
  border-radius: 10px;
  border: 1px solid rgba(157, 78, 221, 0.2);

  .preview-title {
    font-size: 12px;
    font-weight: 600;
    color: #c084fc;
    margin-bottom: 8px;
    display: flex;
    align-items: center;
    gap: 6px;
  }

  .preview-list {
    font-size: 11px;
    color: #a0a0c0;
    margin-bottom: 4px;
  }

  .preview-ul {
    margin: 4px 0 0 16px;
    padding: 0;
    font-size: 11px;
    color: #c084fc;

    li {
      margin-bottom: 2px;
    }
  }
}

.agreement-content {
  max-height: 60vh;
  overflow: auto;

  .agreement-meta {
    color: #7a7a9a;
    font-size: 12px;
    margin-bottom: 12px;
  }

  .agreement-text {
    white-space: pre-wrap;
    word-break: break-word;
    line-height: 1.7;
    font-size: 13px;
    color: #a0a0c0;
  }
}

.dialog-btn {
  padding: 10px 20px;
  border-radius: 10px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
  border: none;
  margin-left: 12px;
  display: inline-flex;
  align-items: center;
  gap: 6px;

  &.cancel-btn {
    background: rgba(157, 78, 221, 0.15);
    border: 1px solid rgba(157, 78, 221, 0.4);
    color: #a0a0c0;

    &:hover {
      background: rgba(157, 78, 221, 0.25);
      color: #c084fc;
    }
  }

  &.confirm-btn {
    background: linear-gradient(135deg, #6546b4, #9d4edd);
    color: #fff;

    &:hover {
      transform: translateY(-1px);
      box-shadow: 0 4px 15px rgba(157, 78, 221, 0.4);
    }

    &:disabled {
      opacity: 0.5;
      cursor: not-allowed;
      transform: none;
    }
  }
}

:deep(.el-checkbox__inner) {
  background: rgba(26, 21, 53, 0.8);
  border-color: rgba(157, 78, 221, 0.4);

  &:hover {
    border-color: #9d4edd;
  }
}

:deep(.el-checkbox__input.is-checked .el-checkbox__inner) {
  background: #9d4edd;
  border-color: #9d4edd;
}

:deep(.el-empty__description p) {
  color: #a0a0c0;
}

@media (max-width: 576px) {
  .btn-text {
    display: none;
  }

  .action-btn {
    padding: 5px 8px;
  }

  .audio-player {
    max-width: 140px;
  }
}
</style>